import { forwardRef, ForwardRefExoticComponent, FC } from 'react'

import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

import { Icon } from '../Icon/index.js'
import type { IconComponentType } from '../Icon/types.js'
import { HTMLTribeProps } from '../types/index.js'
import { BadgeProvider, useBadge } from './BadgeContext.js'

export type BadgeVariant = 'primary' | 'secondary' | 'danger'
export type BadgeSize = 'xs' | 'sm' | 'md' | 'lg'

export type BadgeProps = HTMLTribeProps<'div'> & {
  variant?: BadgeVariant
  size?: BadgeSize
  rounded?: boolean
  leadingIcon?: IconComponentType
  trailingIcon?: IconComponentType
}

type BadgeCompound = ForwardRefExoticComponent<BadgeProps> & {
  Button?: FC<BadgeButtonProps>
}
/**
 * Badges are used to highlight an item's status for quick recognition.
 */
export const Badge: BadgeCompound = forwardRef((props, ref) => {
  const {
    as,
    variant = 'primary',
    size = 'md',
    rounded = false,
    children,
    leadingIcon,
    trailingIcon,
    className,
    ...rest
  } = props

  const colorClsx = [
    variant === 'primary' && 'bg-action-primary-subdued text-content-primary',
    variant === 'secondary' && 'bg-surface-pressed text-content-subdued',
    variant === 'danger' && 'text-content-on-destructive bg-action-destructive',
    'focus:outline-none focus-visible:ring',
  ]

  const sizeClsx = [
    size === 'xs' && 'px-1.5 text-xs',
    size === 'sm' && 'px-1.5 py-0.5 text-xs',
    size === 'md' && 'px-2.5 py-0.5 text-xs',
    size === 'lg' && 'px-3 py-0.5 text-sm',
  ]

  const roundedClsx = {
    rounded: !rounded && size === 'md',
    'rounded-sm': !rounded && (size === 'sm' || size === 'xs'),
    'rounded-md': !rounded && size === 'lg',
    'rounded-full ': rounded,
  }

  let leadingIconElement
  if (leadingIcon) {
    const iconClsx = [
      size === 'md' && !!children && '-ms-0.5 me-1.5',
      size === 'lg' && !!children && '-ms-1 me-1.5',
      'h-4 w-4',
    ]

    leadingIconElement = (
      <Icon className={clsx(iconClsx, colorClsx)}>{leadingIcon}</Icon>
    )
  }
  let trailingIconElement
  if (trailingIcon) {
    const iconClsx = [
      size === 'md' && !!children && '-me-0.5 ms-1.5',
      size === 'lg' && !!children && '-me-1 ms-1.5',
      'h-4 w-4',
    ]
    trailingIconElement = (
      <Icon className={clsx(iconClsx, colorClsx)}>{trailingIcon}</Icon>
    )
  }
  const Component = as || 'span'

  return (
    <Component
      ref={ref}
      className={twMerge(
        clsx(
          'inline-flex items-center',
          'font-medium',
          colorClsx,
          sizeClsx,
          roundedClsx,
          className,
        ),
      )}
      {...rest}
    >
      <BadgeProvider variant={variant} size={size}>
        {leadingIconElement}
        {children}
        {trailingIconElement}
      </BadgeProvider>
    </Component>
  )
})

export type BadgeButtonProps = HTMLTribeProps<'button'>

const BadgeButton: FC<BadgeButtonProps> = props => {
  const { children, className, disabled = false, ...rest } = props

  const { variant } = useBadge()

  const buttonColorClsx = [
    'focus:outline-none',
    disabled && 'opacity-60 cursor-default pointer-events-none',
    variant === 'primary' && [
      'text-content-primary bg-action-primary-subdued focus:bg-action-primary-subdued-focused',
      !disabled &&
        'hover:bg-action-primary-subdued-hovered hover:text-content-primary',
    ],
    variant === 'secondary' && [
      'text-content-subdued bg-surface-subdued focus:outline-none focus:bg-surface focus:text-content-subdued',
      !disabled && ' hover:bg-surface-hovered hover:text-content-hovered',
    ],
  ]

  return (
    <button
      type="button"
      className={clsx(
        'flex-shrink-0 ms-1 h-4 w-4 rounded-full inline-flex items-center justify-center focus:outline-none',
        buttonColorClsx,
        className,
      )}
      disabled={disabled}
      {...rest}
    >
      {children}
    </button>
  )
}

Badge.Button = BadgeButton
